import { HttpContext, HttpContextToken } from '@angular/common/http';
import { NO_SEDE_SEGMENT, sedeCacheSegment } from './active-sede.util';

export const SEDE_ID_HEADER = 'X-Sede-Id';

/**
 * Header de "varias sedes a la vez". El backend SOLO lo acepta en las rutas del dashboard
 * del Owner (400 en cualquier otra) y responde 403 si alguna sede no es del usuario.
 */
export const SEDE_IDS_HEADER = 'X-Sede-Ids';

const SEDE_IDS_SEPARATOR = ',';

/**
 * Alcance de sede EXPLICITO de un request, que pisa a la sede activa global:
 *  - `[]`          → todas las sedes (consolidado): no viaja ningun header de sede.
 *  - `[id]`        → una sede: `X-Sede-Id`, igual que hoy.
 *  - `[id, id...]` → varias: `X-Sede-Ids` y NUNCA `X-Sede-Id`.
 */
export type SedeScope = readonly string[];

/**
 * Viaja por request (HttpContext), no por storage: la seleccion multiple del dashboard no
 * puede tocar la sede activa global que leen Pedidos, Cajas, POS, etc. `null` = sin alcance
 * explicito → el interceptor sigue usando la sede activa global como siempre.
 */
export const SEDE_SCOPE = new HttpContextToken<SedeScope | null>(() => null);

/** Contexto HTTP que lleva el alcance. Sin alcance, un contexto vacio (comportamiento global). */
export function sedeScopeContext(scope?: SedeScope): HttpContext {
  const context = new HttpContext();
  return scope ? context.set(SEDE_SCOPE, scope) : context;
}

/** Headers de sede que corresponden a un alcance explicito (ver `SedeScope`). */
export function sedeScopeHeaders(scope: SedeScope): Record<string, string> {
  const ids = sortedIds(scope);
  const headersBySize: Record<number, Record<string, string>> = {
    0: {},
    1: { [SEDE_ID_HEADER]: ids[0] },
  };
  return headersBySize[ids.length] ?? { [SEDE_IDS_HEADER]: ids.join(SEDE_IDS_SEPARATOR) };
}

/**
 * Segmento de sede para las keys de cache. Con alcance explicito es la lista ORDENADA de
 * sedes (Obrero+Sur y Sur+Obrero son la misma consulta); sin alcance, el de la sede global.
 * Asi una seleccion nunca lee la entrada cacheada de otra.
 */
export function scopedSedeCacheSegment(scope?: SedeScope): string {
  const explicit = scope && scope.length > 0 ? sortedIds(scope).join(SEDE_IDS_SEPARATOR) : NO_SEDE_SEGMENT;
  return scope ? explicit : sedeCacheSegment();
}

function sortedIds(scope: SedeScope): string[] {
  return [...new Set(scope)].sort();
}
